import { PenIcon, TrashIcon } from "@xola/icons";
import { flash, Table } from "@xola/ui-kit";
import { isCrossAccountSellingEnabled, isExperienceProduct, isMembershipProduct, isMerchandiseProduct } from "common";
import { capitalize, sortBy } from "lodash";
import pluralize from "pluralize";
import React, { useMemo, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    Affiliate,
    BookingEngineSource,
    LineItemCode,
    LineItemTemplate,
    LineItemType,
    Permission,
    Product,
    PurchaseRule,
    PurchaseRuleLink,
    PurchaseRuleShortcutTemplateCode,
    SourceCopy,
    XolaPartner,
    XolaPartnerStatus,
} from "types";
import { api } from "../../../api/api";
import { useCollection } from "../../../hooks/useCollection";
import { useInfiniteCollection } from "../../../hooks/useInfiniteCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { InfiniteList } from "../../Common/InfiniteList";
import { IsGranted } from "../../Common/IsGranted";
import { PromptModal } from "../../Common/PromptModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { TableSkeleton } from "../../Common/Skeleton/TableSkeleton";
import { getPurchaseAndPartnerDataFromFilter } from "./TaxAndFeeFormValues";
import { getSelectedItems } from "./TaxAndFeeFormValuesHelper";
import { TermSummary } from "./TermSummary";
import { useProducts } from "@/hooks/resources/useProduct";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";

interface TaxAndFeeListProps {
    filterPurchaseRules?: (purchaseRule: PurchaseRule, purchaseRuleLink?: PurchaseRuleLink) => boolean;
    showDemographicsAndAddOns?: boolean;
    onEditTaxPurchaseRule?: (purchaseRule: PurchaseRule, purchaseRuleLinkId?: ID) => void;
    hideDeleteAction?: boolean;
}

export const TaxAndFeeList = ({
    filterPurchaseRules,
    showDemographicsAndAddOns = true,
    onEditTaxPurchaseRule,
    hideDeleteAction = false,
}: TaxAndFeeListProps = {}) => {
    const history = useHistory();
    const { isGranted } = useAuthorization();
    const invalidate = useInvalidate();

    const isMembershipFeatureEnabled = isGranted(Permission.MEMBERSHIPS_VIEW);
    const isMerchandiseFeatureEnabled = isGranted(Permission.MERCHANDISE_VIEW);
    const canViewChannels = isGranted(Permission.VIEW_PARTNERS_TAX_AND_FEE_SETTING);
    // Base 6 columns: Title, Type, Terms, Sources, Products, Actions.
    const emptyStateColSpan = 6 + (canViewChannels ? 1 : 0) + (showDemographicsAndAddOns ? 2 : 0);

    const [purchaseRuleForDelete, setPurchaseRuleForDelete] = useState<PurchaseRule>();
    const {
        data: purchaseRules,
        fetchNextPage,
        isFetching,
        isLoading,
        hasNextPage,
    } = useInfiniteCollection<PurchaseRule>("purchaseRules", { "tags._id[in]": "fee,tax" });
    const { data: products, isLoading: isProductsLoading } = useProducts();

    const purchaseRuleIds = purchaseRules?.map(({ id }) => id) ?? [];

    const mappedPurchaseRuleTemplateIds = purchaseRules
        .map((purchaseRule) => purchaseRule?.actions[0].template?.id)
        .join(",");

    const { data: lineItemTemplates } = useAutoFetchCollection<LineItemTemplate>(
        "lineItemTemplates",
        {
            "id[in]": mappedPurchaseRuleTemplateIds,
        },
        { enabled: mappedPurchaseRuleTemplateIds.length > 0 },
    );

    const { data: unitLineItemTemplates } = useAutoFetchCollection<LineItemTemplate>("lineItemTemplates", {
        "type[in]": [LineItemType.DEMOGRAPHIC, LineItemType.ADDON, LineItemType.PICKUP].join(","),
    });

    const { data: subTotalLineItemTemplates } = useCollection<LineItemTemplate>("lineItemTemplates", {
        "code[in]": [LineItemCode.DEMOGRAPHIC, LineItemCode.ADDON, LineItemCode.PICKUP].join(","),
    });

    const unitAndSubTotalLineItemTemplateMap = useMemo(() => {
        const templates = [...(unitLineItemTemplates ?? []), ...(subTotalLineItemTemplates ?? [])];
        const map: Record<ID, { type: LineItemType; code: LineItemCode }> = {};
        for (const template of templates) {
            map[template.id] = { type: template.type, code: template.code };
        }

        return map;
    }, [subTotalLineItemTemplates, unitLineItemTemplates]);

    const { data: purchaseRuleLinks, isLoading: isLoadingPurchaseRuleLinks } = useCollection<PurchaseRuleLink>(
        "purchaseRuleLinks",
        { "purchaseRules.purchaseRulePermanentId[in]": purchaseRuleIds.join(",") },
        { enabled: purchaseRuleIds?.length > 0 },
    );

    // O(1) lookup map: purchaseRule.id -> PurchaseRuleLink
    const purchaseRuleLinkMap = useMemo(() => {
        const map = new Map<ID, PurchaseRuleLink>();
        for (const link of purchaseRuleLinks ?? []) {
            for (const rule of link.purchaseRules) {
                if (rule.purchaseRulePermanentId) {
                    map.set(rule.purchaseRulePermanentId, link);
                }
            }
        }
        return map;
    }, [purchaseRuleLinks]);

    const getPurchaseRuleLink = (purchaseRule: PurchaseRule) =>
        purchaseRule.id ? purchaseRuleLinkMap.get(purchaseRule.id) : undefined;

    const filteredPurchaseRules = filterPurchaseRules
        ? purchaseRules.filter((purchaseRule) => filterPurchaseRules(purchaseRule, getPurchaseRuleLink(purchaseRule)))
        : purchaseRules;

    const sortedPurchaseRules = sortBy(filteredPurchaseRules, "createdAt") ?? [];

    const seller = useSeller();
    const shouldShowOperators = isCrossAccountSellingEnabled(seller);
    const { data: agents } = useInfiniteCollection<Affiliate>("affiliates");

    const { data: operators } = useInfiniteCollection<XolaPartner>(
        "partners",
        { expand: "partnerSeller", status: XolaPartnerStatus.ACCEPTED, seller: seller.id },
        { enabled: shouldShowOperators },
    );

    const { mutateAsync: deletePurchaseRule, isLoading: isDeleteLoading } = useMutation(async (id: ID) => {
        return api.delete(`/purchaseRules/${id}`);
    });

    const { mutateAsync: deletePurchaseRuleLink } = useMutation(async (id?: ID) => {
        return api.delete(`/purchaseRuleLinks/${id}`);
    });

    const getLineItemTemplates = (purchaseRule: PurchaseRule) => {
        const id = purchaseRule?.actions[0].template?.id;
        const localLineItemTemplate = lineItemTemplates.find((lineItemTemplate) => lineItemTemplate.id === id);

        return localLineItemTemplate?.name;
    };

    const handleDeleteConfirm = async () => {
        if (purchaseRuleForDelete?.id) {
            await deletePurchaseRule(purchaseRuleForDelete.id);
            await deletePurchaseRuleLink(getPurchaseRuleLinkId(purchaseRuleForDelete, purchaseRuleLinks));
            invalidate("purchaseRules");
            invalidate("purchaseRuleLinks");
            flash.success("Tax and Fee deleted");
        }

        setPurchaseRuleForDelete(undefined);
    };

    if (isLoading || isLoadingPurchaseRuleLinks || isProductsLoading) {
        return (
            <div className="py-4">
                <TableSkeleton rows={6} columns={5} />
            </div>
        );
    }

    return (
        <>
            <InfiniteList
                className="divide-y divide-gray-light"
                isLoading={isFetching}
                hasMore={hasNextPage}
                onLoadMore={fetchNextPage}
            >
                <Table className="w-full table-fixed">
                    <Table.Head>
                        <Table.Row>
                            <Table.Header>Title</Table.Header>
                            <Table.Header className="w-20">Type</Table.Header>
                            <Table.Header>Terms</Table.Header>
                            <Table.Header>Sources</Table.Header>
                            <IsGranted permissions={Permission.VIEW_PARTNERS_TAX_AND_FEE_SETTING}>
                                <Table.Header>Channels</Table.Header>
                            </IsGranted>
                            <Table.Header>Products</Table.Header>
                            {showDemographicsAndAddOns && <Table.Header>Demographics</Table.Header>}
                            {showDemographicsAndAddOns && <Table.Header>AddOns</Table.Header>}
                            <Table.Header className="w-24">Actions</Table.Header>
                        </Table.Row>
                    </Table.Head>
                    <Table.Body>
                        {sortedPurchaseRules.length > 0 ? (
                            sortedPurchaseRules.map((purchaseRule) => (
                                <Table.Row
                                    key={purchaseRule.id}
                                    className="border-b border-gray-lighter"
                                    data-testid="tax-and-fee-list-item"
                                >
                                    <Table.Cell className="no-translate whitespace-pre-line">
                                        {getLineItemTemplates(purchaseRule)}
                                    </Table.Cell>

                                    <Table.Cell>{getTypeText(purchaseRule)}</Table.Cell>

                                    <Table.Cell data-testid="tax-and-fee-list-summary" className="whitespace-pre-line">
                                        <TermSummary purchaseRule={purchaseRule} />
                                    </Table.Cell>

                                    <Table.Cell className="whitespace-pre-line">
                                        {getPurchaseSourceText(purchaseRule)}
                                    </Table.Cell>

                                    <IsGranted permissions={Permission.VIEW_PARTNERS_TAX_AND_FEE_SETTING}>
                                        <Table.Cell
                                            className="no-translate whitespace-pre-line"
                                            data-testid="tax-and-fee-list-channels"
                                        >
                                            {getPartnerText(purchaseRule, agents.length, operators.length)}
                                        </Table.Cell>
                                    </IsGranted>

                                    <Table.Cell className="whitespace-pre-line" data-testid="tax-and-fee-list-listings">
                                        {getListingCountText(
                                            purchaseRule,
                                            purchaseRuleLinks,
                                            products,
                                            isMembershipFeatureEnabled,
                                            isMerchandiseFeatureEnabled,
                                        )}
                                    </Table.Cell>

                                    {showDemographicsAndAddOns && (
                                        <Table.Cell
                                            className="whitespace-pre-line"
                                            data-testid="tax-and-fee-list-demographics"
                                        >
                                            <Demographics
                                                purchaseRule={purchaseRule}
                                                lineItemTemplateMap={unitAndSubTotalLineItemTemplateMap}
                                            />
                                        </Table.Cell>
                                    )}

                                    {showDemographicsAndAddOns && (
                                        <Table.Cell
                                            className="whitespace-pre-line"
                                            data-testid="tax-and-fee-list-add-ons"
                                        >
                                            <AddOns
                                                purchaseRule={purchaseRule}
                                                lineItemTemplateMap={unitAndSubTotalLineItemTemplateMap}
                                            />
                                        </Table.Cell>
                                    )}

                                    <Table.Cell className="space-x-1">
                                        <SecondaryButton
                                            data-testid="tax-and-fee-list-edit-button"
                                            size="tiny"
                                            onClick={() => {
                                                const linkId = getPurchaseRuleLinkId(purchaseRule, purchaseRuleLinks);
                                                if (onEditTaxPurchaseRule) {
                                                    onEditTaxPurchaseRule(purchaseRule, linkId);
                                                    return;
                                                }

                                                history.push(`/settings/taxesAndFees/${purchaseRule.id}/${linkId}`);
                                            }}
                                        >
                                            <PenIcon />
                                        </SecondaryButton>
                                        {!hideDeleteAction && (
                                            <SecondaryButton
                                                data-testid="tax-and-fee-list-delete-button"
                                                size="tiny"
                                                onClick={() => setPurchaseRuleForDelete(purchaseRule)}
                                            >
                                                <TrashIcon />
                                            </SecondaryButton>
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        ) : (
                            <Table.Row>
                                <Table.Cell colSpan={emptyStateColSpan} className="py-4 text-center">
                                    You have not added any taxes or fees yet.
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </InfiniteList>
            {!hideDeleteAction && (
                <PromptModal
                    title="Delete Tax and Fee?"
                    isLoading={isDeleteLoading}
                    isOpen={!!purchaseRuleForDelete}
                    onConfirm={handleDeleteConfirm}
                    onClose={() => setPurchaseRuleForDelete(undefined)}
                >
                    Are you sure you want to delete this Tax and Fee?
                </PromptModal>
            )}
        </>
    );
};

const getListingCountText = (
    purchaseRule: PurchaseRule,
    purchaseRuleLinks: PurchaseRuleLink[],
    products: Product[],
    isMembershipFeatureEnabled?: boolean,
    isMerchandiseFeatureEnabled?: boolean,
) => {
    const link = purchaseRuleLinks.find((link) =>
        link.purchaseRules.some((link) => link.purchaseRulePermanentId === purchaseRule.id),
    );

    const { isAllSelected, selectedExperienceIds, selectedMembershipIds, selectedMerchandiseIds } = getSelectedItems(
        purchaseRule,
        link,
        products,
    );

    // Check if all product types are selected
    if (
        isAllSelected.experiences &&
        (!isMerchandiseFeatureEnabled || isAllSelected.merchandise) &&
        (!isMembershipFeatureEnabled || isAllSelected.memberships)
    ) {
        return "All";
    }

    // Count products based on which types are "all" vs specific selections
    let listingCount = 0;

    listingCount += isAllSelected.experiences
        ? products.filter((product) => isExperienceProduct(product)).length
        : selectedExperienceIds.length;

    listingCount += isAllSelected.memberships
        ? products.filter((product) => isMembershipProduct(product)).length
        : selectedMembershipIds.length;

    listingCount += isAllSelected.merchandise
        ? products.filter((product) => isMerchandiseProduct(product)).length
        : selectedMerchandiseIds.length;

    return (
        <>
            <var data-var="listingCount">{listingCount}</var> {pluralize("Product", listingCount)}
        </>
    );
};

const getTypeText = (purchaseRule: PurchaseRule) => {
    const type = purchaseRule.tags?.[0].id;
    return capitalize(type);
};

const getPurchaseRuleLinkId = (purchaseRule: PurchaseRule, purchaseRuleLinks: PurchaseRuleLink[]) => {
    const link = purchaseRuleLinks.find((link) => {
        return link.purchaseRules.some((link) => link.purchaseRulePermanentId === purchaseRule.id);
    });

    return link?.id;
};

const getPurchaseSourceText = (purchaseRule: PurchaseRule) => {
    const purchaseAndPartnerData = getPurchaseAndPartnerDataFromFilter(purchaseRule?.filter);

    // Filter out the app and integration third party booking engines
    const purchaseSources: string[] = purchaseAndPartnerData.purchaseSources
        .filter((source: any) => ![BookingEngineSource.EXPEDIA, BookingEngineSource.VIATOR].includes(source))
        .map((source: any) => {
            return SourceCopy[source as keyof typeof SourceCopy];
        });

    if (purchaseAndPartnerData.isAppAndIntegrationEnabled) {
        purchaseSources.push("Apps & Integrations");
    }

    return purchaseSources.join(", ");
};

const getPartnerText = (purchaseRule: PurchaseRule, agentCount: number, operatorCount: number) => {
    const purchaseAndPartnerData = getPurchaseAndPartnerDataFromFilter(purchaseRule?.filter);
    const allPartnersSelected = purchaseAndPartnerData.selectedPartners?.all;
    const allOperatorsSelected = purchaseAndPartnerData.selectedOperators?.all;

    if (allPartnersSelected && allOperatorsSelected) {
        return "All";
    }

    const selectedAgents = allPartnersSelected ? agentCount : purchaseAndPartnerData.selectedPartners.selected.length;
    const selectionOperators = allOperatorsSelected
        ? operatorCount
        : purchaseAndPartnerData.selectedOperators.selected.length;
    const partnerCount = selectedAgents + selectionOperators;
    return (
        <>
            <var data-var="partnerCount">{partnerCount}</var> {pluralize("Channel", partnerCount)}
        </>
    );
};

interface AddOnsProps {
    purchaseRule: PurchaseRule;
    lineItemTemplateMap: Record<ID, { type: LineItemType; code: LineItemCode }>;
}

const AddOns = ({ purchaseRule, lineItemTemplateMap }: AddOnsProps) => {
    const templateCodes = purchaseRule.actions[0].shortcut?.options?.templateCodes ?? [];

    const addonTemplateCodes = new Set([
        PurchaseRuleShortcutTemplateCode.ADDON,
        PurchaseRuleShortcutTemplateCode.PICKUP_SUBTOTAL,
        PurchaseRuleShortcutTemplateCode.PRE_TAX_SUBTOTAL,
        PurchaseRuleShortcutTemplateCode.PRE_TAX_FEE_SUBTOTAL,
    ]);

    if (templateCodes.length > 0) {
        const hasAddonCode = templateCodes.some((code) =>
            addonTemplateCodes.has(code as unknown as PurchaseRuleShortcutTemplateCode),
        );

        if (hasAddonCode) {
            return <>On</>;
        }
    }

    const templateIds = purchaseRule?.actions[0].shortcut?.options?.templateIds ?? [];

    if (templateIds.length > 0) {
        const hasAddonType = templateIds.some((id) => {
            const template = lineItemTemplateMap[id];
            if (!template) {
                return false;
            }
            const { type, code } = template;
            return type === LineItemType.ADDON || code === LineItemCode.ADDON;
        });

        return hasAddonType ? <>On</> : <>Off</>;
    }

    return <>Off</>;
};

interface DemographicsProps {
    purchaseRule: PurchaseRule;
    lineItemTemplateMap: Record<ID, { type: LineItemType; code: LineItemCode }>;
}

const Demographics = ({ purchaseRule, lineItemTemplateMap }: DemographicsProps) => {
    const templateCodes = purchaseRule.actions[0].shortcut?.options?.templateCodes ?? [];

    const demographicTemplateCodes = new Set([
        PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC,
        PurchaseRuleShortcutTemplateCode.PRE_TAX_SUBTOTAL,
        PurchaseRuleShortcutTemplateCode.PRE_TAX_FEE_SUBTOTAL,
    ]);

    if (templateCodes.length > 0) {
        const hasDemographicCode = templateCodes.some((code) =>
            demographicTemplateCodes.has(code as unknown as PurchaseRuleShortcutTemplateCode),
        );

        if (hasDemographicCode) {
            return <>On</>;
        }
    }

    const templateIds = purchaseRule?.actions[0].shortcut?.options?.templateIds ?? [];

    if (templateIds.length > 0) {
        const hasDemographicType = templateIds.some((id) => {
            const template = lineItemTemplateMap[id];
            if (!template) {
                return false;
            }
            const { type, code } = template;
            return type === LineItemType.DEMOGRAPHIC || code === LineItemCode.DEMOGRAPHIC;
        });

        return hasDemographicType ? <>On</> : <>Off</>;
    }

    return <>Off</>;
};
